import React, { useCallback, useRef, useState, useEffect } from 'react';
import FormInput from './FormInput';
import SelectInput from './SelectInput';

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Invalid date format:', dateValue);
    return '';
  }
};

const Filters = ({
  onSelectFilters,
  selectedFilters,
  showFilters = { email: true, date: true },
  alexAccessTypes = [],
  hubspotAccessTypes = [],
  cohorts = [],
  companies = [],
}) => {
  const [filter, setFilter] = useState(selectedFilters || {});
  const bulkEmailLimit = parseInt(window.ENV?.BULK_EMAIL_LENGTH || process.env.REACT_APP_BULK_EMAIL_LENGTH || '1', 10);
  const [participants, setParticipants] = useState('All');
  const [status, setStatus] = useState('All');

  const handleCohortChange = useCallback(
    (name, value) => {
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.cohort;
      } else {
        newFilter.cohort = value;
      }
      
      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleAccessTypeChange = useCallback(
    (name, value) => {
      setAccessType(value);
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.accessType;
      } else {
        newFilter.accessType = value;
      }
      
      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleAlexAccessTypeChange = useCallback(
    (name, value) => {
      setAlexAccessType(value);
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.alexAccessType;
      } else {
        newFilter.alexAccessType = value;
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleDateChange = useCallback(
    (dateType, value) => {
      const newFilter = {
        ...filter,
        date: { ...filter?.date, [dateType]: value },
      };

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleCompanyChange = useCallback(
    (name, value) => {
      setCompanyName(value);
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.company_id;
      } else {
        newFilter.company_id = value;
      }
      
      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleParticipantsChange = useCallback(
    (name, value) => {
      setParticipants(value);
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.participants;
      } else {
        newFilter.participants = value;
      }
      
      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleStatusChange = useCallback(
    (name, value) => {
      setStatus(value);
      const newFilter = { ...filter };
      
      if (value === 'All') {
        delete newFilter.status;
      } else {
        newFilter.status = value;
      }
      
      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const cohort = filter?.cohort || 'All';
  const startDate = formatDateForInput(filter?.date?.startDate);
  const userExpirationDate = formatDateForInput(filter?.date?.userExpirationDate);
  const alexExpirationDate = formatDateForInput(filter?.date?.alexExpirationDate);

  const cohortWithAll = [
    { value: 'All', label: 'All' },
    ...cohorts
  ];

  const [alexAccessType, setAlexAccessType] = useState(
    selectedFilters?.alexAccessType || 'All'
  );

  const [accessType, setAccessType] = useState(
    selectedFilters?.accessType || 'All'
  );

  const [companyName, setCompanyName] = useState(
    selectedFilters?.company_id || 'All'
  );

  const alexAccessTypesWithAll = [
    { value: 'All', label: 'All' },
    ...alexAccessTypes
  ];

  const hubspotAccessTypesWithAll = [
    { value: 'All', label: 'All' },
    ...hubspotAccessTypes
  ];

  const companiesWithAll = [
    { value: 'All', label: 'All' },
    ...companies
  ];

  const participantsWithAll = [
    { value: 'All', label: 'All' },
    { value: 'Empty cohorts (0)', label: 'Empty cohorts (0)' },
    { value: 'Small cohorts (1-1000)', label: 'Small cohorts (1-1000)' },
    { value: 'Large cohorts (>1000)', label: 'Large cohorts (>1000)' },
  ];

  const statusWithAll = [
    { value: 'All', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Pending', label: 'Pending'},
    { value: 'Completed', label: 'Completed' },
    { value: 'Unconfirmed', label: 'Unconfirmed' },
    { value: 'Upcoming', label: 'Upcoming' },
  ];

  return (
    <div className="p-4 h-full overflow-y-auto">
      <h3
        className="pb-4"
        style={{ fontSize: '30px', fontWeight: '800' }}
      >
        Filters
      </h3>

      {showFilters?.date && (
        <div>
          <p className="text-sm uppercase text-charcoal font-bold mt-4">Cohort:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="cohort"
              value={cohort}
              options={cohortWithAll}
              onChange={handleCohortChange}
            />
          </div>          
          <p className="text-sm uppercase text-charcoal font-bold mt-6">User Access Type:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="accessType"
              value={accessType}
              options={hubspotAccessTypesWithAll}
              onChange={handleAccessTypeChange}
            />
          </div>          
          <p className="text-sm uppercase text-charcoal font-bold mt-6">Alex Access Type:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="alexAccessType"
              value={alexAccessType}
              options={alexAccessTypesWithAll}
              onChange={handleAlexAccessTypeChange}
            />
          </div>          

          <div className="flex mt-2 mb-4">
            <FormInput
              label="START DATE:"
              name="startDate"
              value={startDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('startDate', e.target.value)
              }
            />
          </div>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="USER EXPIRATION DATE:"
              name="userExpirationDate"
              value={userExpirationDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('userExpirationDate', e.target.value)
              }
            />
          </div>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="ALEX EXPIRATION DATE:"
              name="alexExpirationDate"
              value={alexExpirationDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('alexExpirationDate', e.target.value)
              }
            />
          </div>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">Company:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="company_id"
              value={companyName}
              options={companiesWithAll}
              onChange={handleCompanyChange}
            />
          </div>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">Participants:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="participants"
              value={participants}
              options={participantsWithAll}
              onChange={handleParticipantsChange}
            />
          </div>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">Status:</p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="status"
              value={status}
              options={statusWithAll}
              onChange={handleStatusChange}
            />
          </div>          
        </div>
      )}

      <div className="flex-col pb-4">
        <button
          type="button"
          className="underline cursor-pointer bg-transparent border-none p-0 appearance-none"
          onClick={() => {
            setAlexAccessType('All');
            setAccessType('All');
            setCompanyName('All');
            setParticipants('All');
            setStatus('All');
            setFilter({});
            onSelectFilters({});
          }}
        >
          clear filters
        </button>
      </div>
    </div>
  );
};

export default Filters;
